// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    getGooglePlacesFullAddress,
    GoogleAutoCompleteAddress,
    initGooglePlacesAutoComplete,
} from "@conklin/App/Services/GooglePlaceAutocompleteService";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import React, { useEffect } from "react";
import { connect } from "react-redux";

export interface AddressFieldProps extends TextFieldProps {
    provider?: "none" | "google";
    onAddressSelected?: (address: GoogleAutoCompleteAddress) => void;
}

const mapStateToProps = (state: CustomAppState) => ({
    isGooglePlacesApiLoaded: (state as CustomAppState).xCommon.isGooglePlacesApiLoaded,
});

type Props = AddressFieldProps & ReturnType<typeof mapStateToProps>;

const GoogleAddressField = ({
    provider = "none",
    onAddressSelected,
    disabled,
    isGooglePlacesApiLoaded,
    ...otherProps
}: Props) => {
    const localRef = React.useRef<HTMLInputElement | null>((otherProps.ref as HTMLInputElement | null) || null);

    useEffect(() => {
        if (provider === "none") {
            return;
        }
        if (!isGooglePlacesApiLoaded) {
            return;
        }
        const autocomplete = initGooglePlacesAutoComplete(localRef.current as any, () => {
            onAddressSelected?.(getGooglePlacesFullAddress(autocomplete));
        });
    }, [isGooglePlacesApiLoaded]);

    return (
        <TextField
            {...otherProps}
            {...{ validationRules: "min:1|max:100" }}
            ref={localRef}
            disabled={(!isGooglePlacesApiLoaded && provider !== "none") || disabled}
        />
    );
};

export default connect(mapStateToProps)(GoogleAddressField);
